/**
 * 判断对象是否为初始值
 * @param {Object} obj 目标对象
 * @returns {Boolean} 如果对象为undefined,null,空字符串则返回true,否则返回false
 */
export function isEmpty(obj) {
  return obj === undefined || obj === null || obj === ''
}

/**
 * 拷贝对象属性
 * @param {Object} from
 * @param {Object} to
 * @param {Array<string | Array<string>>} keys 可以是字符串数组或者二维数组，前者表示from和to的key相同，后者表示from和to的key不同
 */
export function assign(from, to, keys) {
  keys.forEach((key) => {
    let fromKey
    let toKey
    if (Array.isArray(key)) {
      fromKey = key[0]
      toKey = key[1]
    } else {
      fromKey = key
      toKey = key
    }
    if (from[fromKey] !== undefined) {
      to[toKey] = from[fromKey]
    }
  })
}

/**
 * 创建新对象
 * @param {Object} obj
 * @param {Array} keys 可以是字符串数组或者二维数组，前者表示from和to的key相同，后者表示from和to的key不同
 */
export function createFrom(obj, keys) {
  const ret = {}
  keys.forEach((key) => {
    let fromKey
    let toKey
    if (Array.isArray(key)) {
      fromKey = key[0]
      toKey = key[1]
    } else {
      fromKey = key
      toKey = key
    }
    if (obj[fromKey] !== undefined) {
      ret[toKey] = obj[fromKey]
    }
  })
  return ret
}

/**
 * 裁剪数组的第一个元素,不修改原数组
 * @param {Array} arr 数组
 * @returns {Array} 如果数组长度大于1则返回去掉第一个元素的数组，否则返回原数组或长度为0的数组
 */
export function trimArrayFirst(arr) {
  if (!arr) return []
  return arr.length > 1 ? arr.slice(1) : arr
}

/**
 * 列表转map
 * @param {Array} list 列表
 * @param {String} key 默认为id
 * @returns {Object} map
 */
export function list2map(list, key = 'id') {
  return list.reduce((map, item) => {
    map[item[key]] = item
    return map
  }, {})
}

export function ifnull(...args) {
  return args.find((arg) => arg !== null && arg !== undefined && arg !== '')
}

/**
 * 计算两个数组的差异
 * @param {Array} newArr 新数组
 * @param {Array} oldArr 老数组
 * @param {Function} cmp 比较函数，返回true表示相等
 */
export function diffarray(newArr, oldArr, cmp = (a, b) => a === b) {
  const diff = { add: [], remove: [] }
  const sub = (a1, a2) => {
    const arr = []
    for (const item of a1) {
      if (!a2.some((item2) => cmp(item, item2))) {
        arr.push(item)
      }
    }
    return arr
  }
  diff.add = sub(newArr, oldArr)
  diff.remove = sub(oldArr, newArr)
  return diff
}

/**
 * dataURL转File对象
 *
 * @param {String} dataurl base64编码的DataURL字符串
 * @param {String} filename 文件名，不包含扩展名
 * @returns {File} 返回一个File对象
 */
export function dataURLtoFile(dataurl, filename) {
  // 验证DataURL格式
  if (!dataurl.startsWith('data:')) {
    throw new Error('无效的DataURL格式')
  }

  const arr = dataurl.split(',')
  const mimeMatch = arr[0].match(/:(.*?);/)

  if (!mimeMatch || mimeMatch.length < 2) {
    throw new Error('无法从DataURL中提取MIME类型')
  }

  const mime = mimeMatch[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  const extension = mime.split('/')[1] || 'bin'
  return new File([u8arr], `${filename}.${extension}`, {
    type: mime,
    lastModified: Date.now()
  })
}

/**
 * 深度克隆
 * @param {object} source 源对象
 * @param {array} excludeTypes 需要排除的对象类型(即不克隆),默认不克隆File
 * @param {array} excludeAttrs 需要排除的属性(即不克隆)
 */
export function deepClone(source, opts) {
  opts = opts || {}
  const excludeTypes = opts.excludeTypes || [File]
  const excludeAttrs = opts.excludeAttrs || []

  // 处理 null、undefined、基本类型
  if (source === null || typeof source !== 'object') {
    return source
  }

  // 检查类型排除
  const typeName = Object.prototype.toString.call(source).slice(8, -1)
  if (excludeTypes.includes(typeName)) {
    return undefined
  }

  // 处理数组
  if (Array.isArray(source)) {
    return source.map((item) => deepClone(item, { excludeTypes, excludeAttrs }))
  }

  // 处理对象
  const target = {}
  for (const key in source) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue
    if (excludeAttrs.includes(key)) continue
    target[key] = deepClone(source[key], { excludeTypes, excludeAttrs })
  }
  return target
}

/**
 * 简单克隆
 */
export function clone(data) {
  return JSON.parse(JSON.stringify(data))
}

export default {
  isEmpty,
  assign,
  createFrom,
  trimArrayFirst,
  list2map,
  ifnull,
  diffarray,
  dataURLtoFile,
  deepClone,
  clone
}
